import { EventEmitter, Timer } from '@visiba/std';
import {
	FormQuestionTypeMultiChoice,
	FormQuestionTypeMultiline,
	FormQuestionTypeSingleChoiceRadio,
	FormQuestionTypeSingleChoiceSelect,
	FormQuestionTypeYesNo,
	QuestionUnion,
} from './form_questions/mod';
import { withTranslation } from '@application/i18n/with_translation';
import { SHEET_CLOSE_INTO_NOTIFICATION_WAIT } from '../../../constants';
import { DynamicFormAnswer } from '@api_patient/dynamic_forms_api.service';
import { LocalPushNotification } from '@component/notification_manager/notification/local_push_notification';
import { NotificationService } from '@component/notification_manager/notification/notification.service';
import { AnalyticService, AnalyticCategories } from '@application/analytic/analytic.service';
import { UnhandledIssuesService } from '@feature/issues/unhandled_issues.service';
import { CellulaFormSubmitData } from '@cellula/react';
import type { FormGraphApiModel, FormOpportunity, PageApiModel, QuestionApiModel } from '@api_patient/generated/clientv2/models';
import { FormDataProvider } from './form_data_providers/form_data_provider';
import { FormDataProviderBasket } from './form_data_providers/form_data_provider_basket';

export interface FormMeta {
	header: string;
	description: string;
	pages: FormPage[];
}

export interface FormState {
	completed: boolean;
	editable: boolean;
	readyState: 'ready' | 'loading' | 'pristine' | 'disposed';
	meta: FormMeta | null;
	pagePosition: number;
}

interface FormSpec {
	completed: boolean;
	formId: number;
	header: string;
	opportunity: FormOpportunity;
	issueTrackingId: string;
	editable?: boolean;
	dataProvider: FormDataProvider;
}

enum FormAction {
	CommitAnswers,
	TurnInAnswers,
	TurnInAnswersLocally,
	Close,
	Move,
	DisplayAnError,
	FillRequiredFields,
}

export class FormWorker {
	public readonly events = new EventEmitter<{
		onChange: Readonly<FormState>;
		onCommit: void;
		onCompleted: void;
		onAbort: void;
		onChangePage: { newPagePosition: number };
	}>();
	public readonly opportunity: FormOpportunity;
	public readonly formId: number;
	public readonly issueTrackingId: string;
	public header: string;
	public editable: boolean;
	public formDataProvider: FormDataProvider;
	private state: FormState;

	constructor(
		spec: FormSpec,
		private readonly unitId: number,
		private readonly notificationService: NotificationService,
		private readonly unhandledIssuesService: UnhandledIssuesService,
		private readonly analyticService: AnalyticService,
	) {
		this.header = spec.header;
		this.formId = spec.formId;
		this.opportunity = spec.opportunity;
		this.issueTrackingId = spec.issueTrackingId;
		this.editable = spec.editable ?? false;
		this.formDataProvider = spec.dataProvider;

		this.state = {
			completed: spec.completed,
			editable: this.editable,
			readyState: 'pristine',
			meta: null,
			pagePosition: -1,
		};
	}

	public async getData(): Promise<void> {
		this.updateData({
			readyState: 'loading',
		});

		const formDataResult = await this.formDataProvider.getForm();
		if (formDataResult.err) {
			this.handleAction(FormAction.DisplayAnError);

			return;
		}

		const answersResult = await this.formDataProvider.getAnswers();
		if (answersResult.err) {
			this.handleAction(FormAction.DisplayAnError);

			return;
		}

		this.updateData({
			readyState: 'ready',
			completed: this.formDataProvider instanceof FormDataProviderBasket ? this.state.completed : answersResult.ok.completed,
			meta: {
				// TODO PAT-426: Allow null?
				description: formDataResult.ok.Description ?? '',
				// TODO PAT-426: Allow null?
				header: formDataResult.ok.Header ?? '',
				// TODO PAT-426: Allow null?
				pages: this.flattenPages(formDataResult.ok, answersResult.ok.answers ?? []),
			},
		});
	}

	public dispose(): void {
		this.updateData({
			readyState: 'disposed',
		});
	}

	public getState(): FormState {
		return this.state;
	}

	public resetPosition(pagePosition = 0): void {
		this.updateData({
			pagePosition,
		});
	}

	public async changePage(direction: 'backwards' | 'forwards', pageData: CellulaFormSubmitData): Promise<void> {
		const { meta, pagePosition } = this.getState();
		const distance = direction === 'forwards' ? 1 : -1;
		const page = meta?.pages[pagePosition];

		if (page == null) return;

		const action = this.evaluateActionToTake(distance);

		if (action === FormAction.Move) {
			this.move(distance);
			this.analyticService.trackEvent(AnalyticCategories.DynamicForm, distance === 1 ? 'Clicked next' : 'Clicked previous');

			return;
		}

		if (action === FormAction.Close) {
			this.events.emit('onAbort', undefined);
			this.analyticService.trackEvent(AnalyticCategories.DynamicForm, 'Clicked close');

			return;
		}

		if (action === FormAction.TurnInAnswersLocally) {
			this.move(distance);
			page.updateValues(pageData);

			return;
		}

		if (action === FormAction.TurnInAnswers) {
			await this.turnInAnswers(page, pageData, false);
			this.move(distance);
			this.analyticService.trackEvent(
				AnalyticCategories.DynamicForm,
				distance === 1 ? 'Clicked next (send page)' : 'Clicked previous (send page)',
			);
			this.events.emit('onChangePage', {
				newPagePosition: pagePosition + distance,
			});

			return;
		}

		if (action === FormAction.CommitAnswers) {
			await this.turnInAnswers(page, pageData, true);
			this.updateData({
				completed: true,
			});
			this.analyticService.trackEvent(AnalyticCategories.DynamicForm, 'Clicked send');

			this.events.emit('onCommit', undefined);

			return;
		}
	}

	public evaluateActionToTake(distance: number): FormAction {
		const { completed, editable } = this.getState();
		const isLastPage = this.isLastPageByDistance(distance);

		if (isLastPage && completed && !editable) {
			return FormAction.Close;
		}

		if (completed && !editable) {
			return FormAction.Move;
		}

		if (isLastPage) {
			return FormAction.CommitAnswers;
		}

		if (distance === -1) {
			return FormAction.TurnInAnswersLocally;
		}

		return FormAction.TurnInAnswers;
	}

	public isFirstPage(): boolean {
		const { pagePosition } = this.getState();
		const isFirstPage = pagePosition === 0;

		return isFirstPage;
	}

	public isFirstOrLastPage(): boolean {
		if (this.isFirstPage()) return true;
		if (this.isLastPage()) return true;

		return false;
	}

	public isLastPage(): boolean {
		const { meta, pagePosition } = this.getState();
		const isLastPage = pagePosition === (meta?.pages.length ?? -1);

		return isLastPage;
	}

	public isLastPageByDistance(distance: number): boolean {
		const { meta, pagePosition } = this.getState();
		const isLastPage = pagePosition + distance === (meta?.pages.length ?? -1);

		return isLastPage;
	}

	private move(distance: number): void {
		const { pagePosition } = this.getState();

		this.updateData({
			pagePosition: pagePosition + distance,
		});
	}

	private async turnInAnswers(page: FormPage, pageData: CellulaFormSubmitData, commit: boolean): Promise<void> {
		const action = await page.turnInAnswers(pageData, commit);
		this.handleAction(action);
	}

	private async markAsCompleted(): Promise<void> {
		let success = true;
		try {
			await this.formDataProvider.setCompletion();

			Timer.wait(() => {
				this.events.emit('onCompleted', undefined);

				this.unhandledIssuesService.removeArbitraryIssue(this.issueTrackingId, 'awaitFormResponses');
			}, SHEET_CLOSE_INTO_NOTIFICATION_WAIT);
		} catch {
			success = false;
			this.notificationService.add(
				new LocalPushNotification({
					label: withTranslation('Feature.DynamicForms.Notification.CommitFailed'),
					type: 'toast',
					variant: 'danger',
				}),
			);
		} finally {
			this.updateData({
				completed: success,
			});
		}
	}

	private flattenPages(data: FormGraphApiModel, allAnswers: DynamicFormAnswer[]): FormPage[] {
		// TODO PAT-426: Allow null?
		const formPages = ((data.PageGroups ?? [])[0]?.Pages ?? []).map(
			(pageModel) => new FormPage(pageModel, allAnswers, this.unitId, this.formDataProvider),
		);

		return formPages;
	}

	private updateData(newState: Partial<FormState>): FormState {
		const mergedState = {
			...this.state,
			...newState,
		};

		this.state = mergedState;
		this.events.emit('onChange', this.state);

		return mergedState;
	}

	private handleAction(action: FormAction): void {
		switch (action) {
			case FormAction.CommitAnswers: {
				this.markAsCompleted();
				break;
			}
			case FormAction.TurnInAnswers: {
				break;
			}
			case FormAction.DisplayAnError: {
				break;
			}
		}
	}
}

export class FormPage {
	public readonly sortValue: number;
	public readonly id: number;
	public readonly header: string;
	public readonly description: string;
	public readonly questions: QuestionUnion[];

	constructor(
		// TODO PAT-426: SortValue does not exist?
		{ Id, Description, Header, Questions, SortValue }: PageApiModel & { SortValue?: number },
		allAnswers: Readonly<DynamicFormAnswer[]>,
		private readonly unitId: number,
		private readonly formDataProvider: FormDataProvider,
	) {
		this.id = Id;
		// TODO PAT-426: Allow null?
		this.description = Description ?? '';
		// TODO PAT-426: Allow null?
		this.header = Header ?? '';
		// TODO PAT-426: Allow null?
		this.questions = (Questions ?? []).map((question) => this.produceQuestion(question));
		// TODO PAT-426: Allow null?
		this.sortValue = SortValue ?? 0;

		this.updateDefaultValues(allAnswers);
	}

	public updateValues(pageData: CellulaFormSubmitData): DynamicFormAnswer[] {
		const values = this.questions.map((question) => question.getAnswer(pageData));

		this.updateDefaultValues(values);

		return values;
	}

	public async turnInAnswers(pageData: CellulaFormSubmitData, commit = false): Promise<FormAction> {
		const values = this.updateValues(pageData);

		await this.formDataProvider.answer(values);

		if (commit) {
			return FormAction.CommitAnswers;
		}

		return FormAction.TurnInAnswers;
	}

	private produceQuestion(question: QuestionApiModel): QuestionUnion {
		switch (question.Type) {
			case 'MultiChoice':
				return new FormQuestionTypeMultiChoice(question);
			case 'Multiline':
				return new FormQuestionTypeMultiline(question);
			case 'SingleChoiceRadio':
				return new FormQuestionTypeSingleChoiceRadio(question);
			case 'SingleChoiceSelect':
				return new FormQuestionTypeSingleChoiceSelect(question);
			case 'YesNo':
				return new FormQuestionTypeYesNo(question);
			default:
				throw new Error(`${question.Type} was not expected`);
		}
	}

	private updateDefaultValues(allAnswers: Readonly<DynamicFormAnswer[]>): void {
		for (const question of this.questions) {
			question.setDefault(allAnswers);
		}
	}
}
